import type { Skill } from "@cohesive/api-entities";
import { ScrollText } from "lucide-react";
import { Icon } from "../../Icon";
import {
  BaseEntityCard,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardDescription,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardProps,
  BaseEntityCardTitle,
} from "../BaseCard";

export type { Skill };

export type SkillCardEntityProps = Skill &
  BaseEntityCardProps & {
    /** Optional badge shown alongside the name, e.g. "New skill" or "Skill updated" */
    badge?: string;
  };

function SkillIcon({ icon }: { icon?: Skill["icon"] }) {
  const emoji = icon?.type === "emoji" ? icon.emoji : null;
  if (emoji) {
    return <span className="text-base leading-none">{emoji}</span>;
  }
  if (icon?.type && icon.type !== "emoji") {
    return <Icon icon={icon as any} size={16} />;
  }
  return <ScrollText className="h-4 w-4 text-primary/70" />;
}

export function SkillCardEntity({
  id: _id,
  name,
  description,
  icon,
  badge,
  onClick,
  onDoubleClick,
  actions,
  processes,
  className,
  style,
}: SkillCardEntityProps) {
  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
      align="center"
      actions={actions}
    >
      <BaseEntityCardIcon className="rounded-lg bg-primary/[0.07] dark:bg-primary/[0.12] shrink-0">
        <SkillIcon icon={icon} />
      </BaseEntityCardIcon>

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle className="text-sm font-medium leading-snug">
            {name}
            {badge && (
              <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                {badge}
              </span>
            )}
          </BaseEntityCardTitle>
        </BaseEntityCardHeader>

        {description && (
          <BaseEntityCardBody>
            <BaseEntityCardDescription>{description}</BaseEntityCardDescription>
          </BaseEntityCardBody>
        )}
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
